import { Stand } from "@/shared/models/Stand";
import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { MillAdjustmentByShift } from "../models/MillAdjustmentByShift";
const moment = getMomentTz();

export const getAdjustmentsByShift = async (
	params: {
		shiftId: number | null;
		millAdjustmentId?: number | null;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	if (params.shiftId) {
		parameters.push({ name: "ShiftId", value: params.shiftId });
	}

	if (params.millAdjustmentId) {
		parameters.push({
			name: "MillAdjustmentId",
			value: params.millAdjustmentId,
		});
	}

	const resp = await apiService.callV2(
		"[M2].[GetMillAdjustments]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((delay: MillAdjustmentByShift) => ({
			...delay,
			Timestamp: delay.Timestamp
				? moment.utc(delay["Timestamp"]).toDate()
				: null,
			Start: delay.Start ? moment.utc(delay["Start"]).toDate() : null,
			End: delay.End ? moment.utc(delay["End"]).toDate() : null,
		}));

		return rows as MillAdjustmentByShift[];
	} else {
		throw new Error(resp.message || "Error fetching adjustments");
	}
};

export const getAdjustmentsByDelay = async (
	params: { delayId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "DelayId", value: params.delayId },
	];

	const resp = await apiService.callV2(
		"[M2].[GetMillAdjustmentsByDelay]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((delay: MillAdjustmentByShift) => ({
			...delay,
			Timestamp: delay.Timestamp
				? moment.utc(delay["Timestamp"]).toDate()
				: null,
			Start: delay.Start ? moment.utc(delay["Start"]).toDate() : null,
			End: delay.End ? moment.utc(delay["End"]).toDate() : null,
		}));

		return rows as MillAdjustmentByShift[];
	} else {
		throw new Error(resp.message || "Error fetching adjustments");
	}
};

type Props = {
	millAdjustmentId: number | null;
	timestamp: Date;
	adjustmentTypeId: number;
	adjustmentReasonId: number;
	operatorId: number;
	adjustmentValue: number | null;
	unit: string | null;
	comments: string;
	delayId: number | null;
};

export const upsertMillAdjustment = async ({
	millAdjustmentId,
	timestamp,
	adjustmentTypeId,
	adjustmentReasonId,
	operatorId,
	adjustmentValue,
	unit,
	comments,
	delayId,
}: Props) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "MillAdjustmentId", value: millAdjustmentId },
		{ name: "Comments", value: comments },
	];

	if (timestamp) {
		parameters.push({
			name: "Timestamp",
			value: timestamp ? timestamp.toISOString() : null,
		});
	}
	if (adjustmentTypeId) {
		parameters.push({ name: "AdjustmentTypeId", value: adjustmentTypeId });
	}
	if (adjustmentReasonId) {
		parameters.push({ name: "AdjustmentReasonId", value: adjustmentReasonId });
	}
	if (operatorId) {
		parameters.push({ name: "OperatorId", value: operatorId });
	}
	if (adjustmentValue) {
		parameters.push({ name: "AdjustmentValue", value: adjustmentValue });
	}
	if (unit) {
		parameters.push({ name: "Unit", value: unit });
	}
	if (delayId) {
		parameters.push({ name: "DelayId", value: delayId });
	}

	const resp = await apiService.callV2(
		"[M2].[UpsertMillAdjustment]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error upserting adjustments");
	}
};

export const deleteAdjustment = async (adjustmentId: number | null) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "MillAdjustmentId", value: adjustmentId },
	];

	const resp = await apiService.callV2(
		"[M2].[DeleteMillAdjustment]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting adjustments");
	}
};

export const getStandsByMillAdjustment = async (
	params: { millAdjustmentId: number | null },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "MillAdjustmentId", value: params.millAdjustmentId },
	];

	const resp = await apiService.callV2(
		"[M2].[GetStandsByMillAdjustment]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as Stand[];
	} else {
		throw new Error(resp.message || "Error fetching stands");
	}
};

export const setStandsToMillAdjustment = async (
	millAdjustmentId: number,
	stand: Stand[],
) => {
	const apiService = new MESApiService();

	const standsIds = stand.map((item) => item.StandId);
	const standIdsString = standsIds.join(",");

	const parameters: spParameter[] = [
		{ name: "MillAdjustmentId", value: millAdjustmentId },
		{ name: "StandIds", value: standIdsString },
	];

	return await apiService.callV2(
		"[M2].[SetStandsToMillAdjustment]",
		parameters,
	);
};
